import { getFromS3 } from '@/services/AWSService';
import { store } from '@/store';
import { logInfo, logError } from '@/utilities/Filesystem';

interface NetworkTimeoutConfig {
    speedTestTimeoutMs: number;
    minTimeoutMs: number;
    maxTimeoutMs: number;
    speedTestFileSize: number;
    languageFileEstimatedSize: number;
    manifestFileEstimatedSize: number;
    bundleManifestEstimatedSize: number;
    bundleSignatureEstimatedSize: number;
    bundleFileEstimatedSize: number;
    fallbackToLocalThresholdMbps: number;
}

interface SpeedTestResult {
    speedBytesPerMs: number;
    downloadTimeMs: number;
    speedMbps: number;
    recommendedTimeouts: {
        language: number;
        manifest: number;
        bundleManifest: number;
        bundleSignature: number;
        bundle: number;
    };
    shouldFallbackToLocal: boolean;
}

export class NetworkSpeedService {
    private cachedSpeedTest: SpeedTestResult | null = null;
    private lastTestTime: number = 0;
    private readonly CACHE_DURATION_MS = 60000; // Cache for 1 minute

    /**
     * Get timeout configuration from language file, with sensible defaults
     */
    private getTimeoutConfig(): NetworkTimeoutConfig {
        const messages = store.getters.getLocaleMessages();
        const config = messages?.buildConfigs?.config?.networkTimeouts;

        return {
            speedTestTimeoutMs: config?.speedTestTimeoutMs || 20000, // 20s - if it times out, connection is very slow
            minTimeoutMs: config?.minTimeoutMs || 15000,
            maxTimeoutMs: config?.maxTimeoutMs || 1800000, // 30 minutes for background downloads on very slow connections
            speedTestFileSize: config?.speedTestFileSize || 51200, // 50KB
            languageFileEstimatedSize: config?.languageFileEstimatedSize || 6000000,
            manifestFileEstimatedSize: config?.manifestFileEstimatedSize || 1500000,
            bundleManifestEstimatedSize: config?.bundleManifestEstimatedSize || 500,
            bundleSignatureEstimatedSize: config?.bundleSignatureEstimatedSize || 256,
            bundleFileEstimatedSize: config?.bundleFileEstimatedSize || 8000000,
            fallbackToLocalThresholdMbps: config?.fallbackToLocalThresholdMbps || 0.5, // Below 0.5 Mbps, use cache + background download
        };
    }

    /**
     * Perform speed test by downloading speedtest-50kb.json (~50KB)
     * Using a 50KB file gives much more accurate results because:
     * - Large enough that transfer time dominates HTTP overhead
     * - Small enough to download quickly even on slow connections
     * - Provides realistic measurement of sustained download speed
     * Returns speed in bytes per millisecond
     */
    private async performSpeedTest(): Promise<SpeedTestResult | null> {
        const config = this.getTimeoutConfig();
        
        logInfo(`[NetworkSpeedService] ===== Starting Network Speed Test =====`, 2);
        logInfo(`[NetworkSpeedService] Test file: speedtest-50kb.json (~${config.speedTestFileSize} bytes)`, 2);
        logInfo(`[NetworkSpeedService] Timeout: ${config.speedTestTimeoutMs}ms`, 2);
        
        const result = await this.attemptSpeedTest(config.speedTestTimeoutMs);
        
        logInfo(`[NetworkSpeedService] ===== Speed Test Complete =====`, 2);
        return result;
    }

    /**
     * Attempt a single speed test with the given timeout
     */
    private async attemptSpeedTest(timeoutMs: number): Promise<SpeedTestResult | null> {
        const config = this.getTimeoutConfig();
        const variant = store.state.variant;
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        const rootFolder = store.getters.isPwa() && host === 'localhost' ? 'demopattern' : variant;

        console.log(`[NetworkSpeedService] 🚀 Starting speed test with speedtest-50kb.json (~${(config.speedTestFileSize/1024).toFixed(0)}KB), timeout: ${timeoutMs}ms`);
        logInfo(`[NetworkSpeedService] Starting speed test attempt with timeout: ${timeoutMs}ms`, 2);

        const startTime = Date.now();

        try {
            logInfo(`[NetworkSpeedService] Downloading test file from variant: ${rootFolder}`, 2);
            
            // Download 50KB test file
            const response = await getFromS3(
                rootFolder,
                `speedtest-50kb.json?timestamp=${startTime}`,
                'json',
                false,
                timeoutMs // Use provided timeout
            );

            const endTime = Date.now();
            const downloadTimeMs = endTime - startTime;

            console.log(`[NetworkSpeedService] 🕐 Raw download time: ${downloadTimeMs}ms`);
            logInfo(`[NetworkSpeedService] Download completed in ${downloadTimeMs}ms`, 2);

            // Validate we got data
            if (!response.data) {
                console.log('[NetworkSpeedService] ❌ Speed test failed - no data returned');
                logInfo('[NetworkSpeedService] Speed test failed - no data returned', 2);
                return null;
            }
            
            logInfo('[NetworkSpeedService] Response data received, validating size...', 2);

            // Get actual response size from the downloaded data
            const actualSize = response.headers?.['content-length'] 
                ? parseInt(response.headers['content-length']) 
                : config.speedTestFileSize;
            
            const dataString = JSON.stringify(response.data);
            const estimatedSize = new Blob([dataString]).size;
            
            console.log(`[NetworkSpeedService] 📦 File size - Configured: ${config.speedTestFileSize} bytes, Content-Length header: ${actualSize} bytes, Actual response: ${estimatedSize} bytes`);
            logInfo(`[NetworkSpeedService] File sizes - Configured: ${config.speedTestFileSize}, Content-Length: ${actualSize}, Estimated: ${estimatedSize}`, 2);

            // Validate that we got actual data, not an error response
            if (estimatedSize < 1000) {
                console.error(`[NetworkSpeedService] ❌ Speed test file appears invalid! Expected ~50KB, got ${estimatedSize} bytes`);
                console.error(`[NetworkSpeedService] ❌ The file 'speedtest-50kb.json' likely doesn't exist on S3 yet.`);
                console.error(`[NetworkSpeedService] 💡 Upload speedtest-50kb.json to S3 and try again.`);
                
                // Log single-line summary
                logInfo(`[NetworkSpeedService] SPEED TEST RESULT: FILE MISSING (got ${estimatedSize} bytes, expected ~50KB) - Will use local cache`, 0);
                
                // Return a result that will trigger local-only mode
                const fallbackResult: SpeedTestResult = {
                    speedBytesPerMs: 0,
                    downloadTimeMs: downloadTimeMs,
                    speedMbps: 0,
                    recommendedTimeouts: {
                        language: config.maxTimeoutMs,
                        manifest: config.maxTimeoutMs,
                        bundleManifest: config.maxTimeoutMs,
                        bundleSignature: config.maxTimeoutMs,
                        bundle: config.maxTimeoutMs,
                    },
                    shouldFallbackToLocal: true,
                };
                this.cachedSpeedTest = fallbackResult;
                this.lastTestTime = Date.now();
                return fallbackResult;
            }

            // IMPORTANT: Use the actual estimated size from response data for accurate calculation
            const fileSizeToUse = estimatedSize > 0 ? estimatedSize : config.speedTestFileSize;
            
            console.log(`[NetworkSpeedService] ✓ Using ACTUAL size for calculation: ${fileSizeToUse.toLocaleString()} bytes (${(fileSizeToUse/1024).toFixed(1)}KB)`);
            logInfo(`[NetworkSpeedService] Using actual file size for calculation: ${fileSizeToUse} bytes`, 2);

            // For very fast downloads (< 10ms), the overhead dominates
            // Use a minimum time to avoid artificially low results
            const adjustedTimeMs = Math.max(downloadTimeMs, 10);
            
            if (downloadTimeMs < 10) {
                console.log(`[NetworkSpeedService] ⚡ Very fast download (${downloadTimeMs}ms). Using minimum 10ms for calculation to account for overhead.`);
                logInfo(`[NetworkSpeedService] Very fast download (${downloadTimeMs}ms), using minimum 10ms for calculation`, 2);
            }

            // Calculate speed: bytes per millisecond
            const speedBytesPerMs = fileSizeToUse / adjustedTimeMs;
            logInfo(`[NetworkSpeedService] Calculated speed: ${speedBytesPerMs.toFixed(4)} bytes/ms`, 2);

            console.log(`[NetworkSpeedService] ✅ Speed test complete: ${downloadTimeMs}ms for ${fileSizeToUse.toLocaleString()} bytes (${(fileSizeToUse/1024).toFixed(1)}KB)`);
            logInfo(
                `[NetworkSpeedService] Speed test complete: ${downloadTimeMs}ms for ${fileSizeToUse} bytes`,
                1
            );
            console.log(`[NetworkSpeedService] 📊 Calculated speed: ${speedBytesPerMs.toFixed(4)} bytes/ms = ${(speedBytesPerMs * 1000).toFixed(2)} KB/s (using ${adjustedTimeMs}ms)`);
            logInfo(`[NetworkSpeedService] Calculated speed: ${speedBytesPerMs.toFixed(4)} bytes/ms`, 1);
            
            // Calculate speed in Mbps for readability
            const speedMbps = (speedBytesPerMs * 8) / 1000;
            console.log(`[NetworkSpeedService] 🌐 Effective connection speed: ${speedMbps.toFixed(3)} Mbps`);
            console.log(`[NetworkSpeedService] 💡 Note: For small files, this includes HTTP overhead (DNS, SSL, headers)`);
            logInfo(`[NetworkSpeedService] Equivalent to: ${speedMbps.toFixed(3)} Mbps`, 1);
            logInfo(`[NetworkSpeedService] Connection speed (Mbps): ${speedMbps.toFixed(3)}`, 2);

            // Calculate recommended timeouts for different file sizes
            const recommendedTimeouts = {
                language: this.calculateTimeout(
                    config.languageFileEstimatedSize,
                    speedBytesPerMs,
                    config.minTimeoutMs,
                    config.maxTimeoutMs
                ),
                manifest: this.calculateTimeout(
                    config.manifestFileEstimatedSize,
                    speedBytesPerMs,
                    config.minTimeoutMs,
                    config.maxTimeoutMs
                ),
                bundleManifest: this.calculateTimeout(
                    config.bundleManifestEstimatedSize,
                    speedBytesPerMs,
                    config.minTimeoutMs,
                    config.maxTimeoutMs
                ),
                bundleSignature: this.calculateTimeout(
                    config.bundleSignatureEstimatedSize,
                    speedBytesPerMs,
                    config.minTimeoutMs,
                    config.maxTimeoutMs
                ),
                bundle: this.calculateTimeout(
                    config.bundleFileEstimatedSize,
                    speedBytesPerMs,
                    config.minTimeoutMs,
                    config.maxTimeoutMs
                ),
            };

            console.log(`[NetworkSpeedService] ⏱️  Timeouts - Language(6MB): ${(recommendedTimeouts.language/1000).toFixed(0)}s, Manifest(1.5MB): ${(recommendedTimeouts.manifest/1000).toFixed(0)}s, Bundle(8MB): ${(recommendedTimeouts.bundle/1000).toFixed(0)}s`);
            logInfo(
                `[NetworkSpeedService] Recommended timeouts - Language(6MB): ${recommendedTimeouts.language}ms, Manifest(1.5MB): ${recommendedTimeouts.manifest}ms, Bundle(8MB): ${recommendedTimeouts.bundle}ms`,
                1
            );
            
            // Determine if connection is too slow for downloads
            const shouldFallbackToLocal = speedMbps < config.fallbackToLocalThresholdMbps;
            
            logInfo(`[NetworkSpeedService] Evaluating fallback threshold: ${config.fallbackToLocalThresholdMbps} Mbps`, 2);
            
            if (shouldFallbackToLocal) {
                console.log(`[NetworkSpeedService] ⚠️  Connection too slow (${speedMbps.toFixed(3)} Mbps < ${config.fallbackToLocalThresholdMbps} Mbps). Will use local cache.`);
                logInfo(
                    `[NetworkSpeedService] Connection too slow (${speedMbps.toFixed(3)} Mbps < ${config.fallbackToLocalThresholdMbps} Mbps threshold). Recommend using local data.`,
                    1
                );
                logInfo(`[NetworkSpeedService] Decision: Use local cache`, 2);
            } else {
                console.log(`[NetworkSpeedService] ✅ Connection fast enough (${speedMbps.toFixed(3)} Mbps > ${config.fallbackToLocalThresholdMbps} Mbps). Will attempt downloads.`);
                logInfo(`[NetworkSpeedService] Connection fast enough, will attempt downloads`, 2);
            }

            const result: SpeedTestResult = {
                speedBytesPerMs,
                downloadTimeMs,
                speedMbps,
                recommendedTimeouts,
                shouldFallbackToLocal,
            };

            // Cache the result
            this.cachedSpeedTest = result;
            this.lastTestTime = Date.now();
            
            // Log single-line summary of speed test result
            logInfo(`[NetworkSpeedService] SPEED TEST RESULT: ${speedMbps.toFixed(3)} Mbps (${downloadTimeMs}ms for ${(fileSizeToUse/1024).toFixed(1)}KB) - ${shouldFallbackToLocal ? 'TOO SLOW - Use local cache' : 'OK - Will attempt downloads'}`, 0);
            
            logInfo(`[NetworkSpeedService] Speed test result cached for ${this.CACHE_DURATION_MS}ms`, 2);

            return result;
        } catch (error: any) {
            console.log(`[NetworkSpeedService] ❌ Speed test failed: ${error.message}`);
            logError(error, '[NetworkSpeedService] Speed test failed');
            
            // Log single-line summary of speed test failure
            logInfo(`[NetworkSpeedService] SPEED TEST RESULT: FAILED (${error.message}) - Will use local cache`, 0);

            // If speed test fails, assume slow connection and use conservative timeouts
            const config = this.getTimeoutConfig();
            console.log(`[NetworkSpeedService] 🔄 Using fallback: max timeout ${config.maxTimeoutMs}ms, will use local cache`);
            logInfo(`[NetworkSpeedService] Using fallback configuration due to test failure`, 2);
            return {
                speedBytesPerMs: 0,
                downloadTimeMs: config.speedTestTimeoutMs,
                speedMbps: 0,
                recommendedTimeouts: {
                    language: config.maxTimeoutMs,
                    manifest: config.maxTimeoutMs,
                    bundleManifest: config.maxTimeoutMs,
                    bundleSignature: config.maxTimeoutMs,
                    bundle: config.maxTimeoutMs,
                },
                shouldFallbackToLocal: true, // Assume we should fallback if speed test fails
            };
        }
    }

    /**
     * Calculate timeout for a file based on size and measured speed
     * Adds 50% buffer for safety
     */
    private calculateTimeout(
        fileSizeBytes: number,
        speedBytesPerMs: number,
        minTimeoutMs: number,
        maxTimeoutMs: number
    ): number {
        if (speedBytesPerMs <= 0) {
            // Unknown speed, use max timeout
            return maxTimeoutMs;
        }

        // Calculate expected download time
        const expectedTimeMs = fileSizeBytes / speedBytesPerMs;

        // Add 50% buffer for safety
        const timeoutWithBuffer = expectedTimeMs * 1.5;

        // Clamp between min and max
        const timeout = Math.max(minTimeoutMs, Math.min(maxTimeoutMs, timeoutWithBuffer));

        return Math.round(timeout);
    }

    /**
     * Get recommended timeout for a specific file type
     * Uses cached result if available and recent
     */
    public async getRecommendedTimeout(
        fileType: 'language' | 'manifest' | 'bundleManifest' | 'bundleSignature' | 'bundle'
    ): Promise<number> {
        logInfo(`[NetworkSpeedService] getRecommendedTimeout called for: ${fileType}`, 2);
        
        // Check if we have a recent cached result
        const now = Date.now();
        if (this.cachedSpeedTest && (now - this.lastTestTime) < this.CACHE_DURATION_MS) {
            const timeout = this.cachedSpeedTest.recommendedTimeouts[fileType];
            const cacheAge = Math.round((now - this.lastTestTime) / 1000);
            console.log(`[NetworkSpeedService] ⏱️  Recommended timeout for ${fileType}: ${(timeout/1000).toFixed(0)}s (cached)`);
            logInfo(`[NetworkSpeedService] Using cached speed test (age: ${cacheAge}s)`, 2);
            logInfo(`[NetworkSpeedService] Returning cached timeout for ${fileType}: ${timeout}ms`, 2);
            return timeout;
        }

        logInfo(`[NetworkSpeedService] No valid cached result, performing new speed test`, 2);
        
        // Perform new speed test
        const result = await this.performSpeedTest();

        if (!result) {
            // Speed test failed, use max timeout
            const config = this.getTimeoutConfig();
            console.log(`[NetworkSpeedService] ⚠️  Speed test failed, using max timeout: ${(config.maxTimeoutMs/1000).toFixed(0)}s`);
            logInfo(`[NetworkSpeedService] Speed test failed, using max timeout: ${config.maxTimeoutMs}ms`, 1);
            logInfo(`[NetworkSpeedService] Returning fallback max timeout: ${config.maxTimeoutMs}ms`, 2);
            return config.maxTimeoutMs;
        }

        const timeout = result.recommendedTimeouts[fileType];
        console.log(`[NetworkSpeedService] ⏱️  Recommended timeout for ${fileType}: ${(timeout/1000).toFixed(0)}s`);
        logInfo(`[NetworkSpeedService] Returning calculated timeout for ${fileType}: ${timeout}ms`, 2);
        return timeout;
    }

    /**
     * Check if we should attempt to download a file based on connection speed
     * Returns true if connection is fast enough, false if should use local cache
     */
    public async shouldAttemptDownload(fileType: 'language' | 'manifest' | 'bundle'): Promise<boolean> {
        console.log(`[NetworkSpeedService] 🔍 Checking if should attempt ${fileType} download...`);
        logInfo(`[NetworkSpeedService] shouldAttemptDownload called for: ${fileType}`, 2);
        
        // Check if we have a recent cached result
        const now = Date.now();
        if (!this.cachedSpeedTest || (now - this.lastTestTime) >= this.CACHE_DURATION_MS) {
            console.log('[NetworkSpeedService] 📊 No cached speed test, performing new test...');
            logInfo('[NetworkSpeedService] No cached speed test available, will perform new test', 2);
            
            // Perform speed test if we don't have recent data
            const result = await this.performSpeedTest();
            
            if (!result) {
                // Speed test failed, assume we should use local
                console.log('[NetworkSpeedService] ❌ Speed test failed, will use local cache');
                logInfo('[NetworkSpeedService] Speed test failed, recommending local fallback', 1);
                logInfo('[NetworkSpeedService] Decision: Do NOT attempt download, use local', 2);
                return false;
            }
        } else {
            const age = Math.round((now - this.lastTestTime) / 1000);
            console.log(`[NetworkSpeedService] ✅ Using cached speed test (${age}s old): ${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps`);
            logInfo(`[NetworkSpeedService] SPEED TEST CACHED (${age}s old): ${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps - ${this.cachedSpeedTest!.shouldFallbackToLocal ? 'TOO SLOW' : 'OK'}`, 0);
        }
        
        const config = this.getTimeoutConfig();
        
        // If connection is below threshold, don't attempt large downloads
        logInfo(`[NetworkSpeedService] Checking fallback threshold: ${config.fallbackToLocalThresholdMbps} Mbps`, 2);
        
        if (this.cachedSpeedTest!.shouldFallbackToLocal) {
            console.log(
                `[NetworkSpeedService] ⛔ Connection too slow (${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps < ${config.fallbackToLocalThresholdMbps} Mbps). ` +
                `Skipping ${fileType} download.`
            );
            logInfo(
                `[NetworkSpeedService] Connection too slow (${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps). ` +
                `Skipping ${fileType} download.`,
                1
            );
            logInfo(`[NetworkSpeedService] Decision: Do NOT attempt download, use local`, 2);
            return false;
        }
        
        // For bundles, be more conservative (require at least 0.5 Mbps)
        if (fileType === 'bundle' && this.cachedSpeedTest!.speedMbps < 0.5) {
            const estimatedTime = (config.bundleFileEstimatedSize / this.cachedSpeedTest!.speedBytesPerMs) / 1000;
            console.log(
                `[NetworkSpeedService] ⛔ Connection too slow for bundle (${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps < 0.5 Mbps). ` +
                `Estimated: ${estimatedTime.toFixed(0)}s. Deferring.`
            );
            logInfo(
                `[NetworkSpeedService] Connection too slow for bundle download (${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps < 0.5 Mbps). ` +
                `Estimated download time: ${estimatedTime.toFixed(0)}s. Deferring bundle update.`,
                1
            );
            logInfo(`[NetworkSpeedService] Bundle-specific check: speed ${this.cachedSpeedTest!.speedMbps.toFixed(3)} < 0.5 Mbps threshold`, 2);
            logInfo(`[NetworkSpeedService] Decision: Do NOT attempt bundle download`, 2);
            return false;
        }
        
        // For language files (6MB), warn if it will take > 2 minutes
        if (fileType === 'language') {
            const estimatedTime = (config.languageFileEstimatedSize / this.cachedSpeedTest!.speedBytesPerMs) / 1000;
            console.log(`[NetworkSpeedService] 📥 Will download ${fileType} (estimated: ${estimatedTime.toFixed(0)}s at ${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps)`);
            logInfo(`[NetworkSpeedService] Language file estimated download time: ${estimatedTime.toFixed(0)}s`, 2);
            
            if (estimatedTime > 120) {
                console.log(`[NetworkSpeedService] ⚠️  This will take over 2 minutes on current connection`);
                logInfo(
                    `[NetworkSpeedService] Language file download will take ~${estimatedTime.toFixed(0)}s. ` +
                    `Connection speed: ${this.cachedSpeedTest!.speedMbps.toFixed(3)} Mbps`,
                    1
                );
                logInfo(`[NetworkSpeedService] Warning: Download will exceed 2 minutes`, 2);
            }
        }
        
        console.log(`[NetworkSpeedService] ✅ Will attempt ${fileType} download`);
        logInfo(`[NetworkSpeedService] Decision: WILL attempt ${fileType} download`, 2);
        return true;
    }

    /**
     * Clear cached speed test to force a new test on next call
     */
    public clearCache(): void {
        this.cachedSpeedTest = null;
        this.lastTestTime = 0;
        logInfo('[NetworkSpeedService] Speed test cache cleared', 2);
    }

    /**
     * Get the most recent speed test result without performing a new test
     */
    public getCachedResult(): SpeedTestResult | null {
        return this.cachedSpeedTest;
    }
    
    /**
     * Get estimated download time for a file type in seconds
     */
    public getEstimatedDownloadTime(fileType: 'language' | 'manifest' | 'bundle'): number | null {
        if (!this.cachedSpeedTest) {
            return null;
        }
        
        const config = this.getTimeoutConfig();
        let fileSize = 0;
        
        switch (fileType) {
            case 'language':
                fileSize = config.languageFileEstimatedSize;
                break;
            case 'manifest':
                fileSize = config.manifestFileEstimatedSize;
                break;
            case 'bundle':
                fileSize = config.bundleFileEstimatedSize;
                break;
        }
        
        const timeMs = fileSize / this.cachedSpeedTest.speedBytesPerMs;
        return Math.round(timeMs / 1000);
    }
}

// Export singleton instance
export const networkSpeedService = new NetworkSpeedService();

